const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), [role="combobox"], [role="textbox"], [role="button"]';

const isElementVisible = element => {
  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    element.offsetParent !== null
  );
};

const getFocusableElements = container => {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    isElementVisible
  );
};

const focusButtonById = (buttonId, secondaryButtonId = null) => {
  setTimeout(() => {
    const buttonElement = document.getElementById(buttonId);

    if (buttonElement) {
      buttonElement.setAttribute("tabIndex", "0");
      buttonElement.focus();
    }

    // Also make the secondary button focusable
    if (secondaryButtonId) {
      const secondaryElement = document.getElementById(secondaryButtonId);
      if (secondaryElement) {
        secondaryElement.setAttribute("tabIndex", "0");
      }
    }
  }, 0);
};

const focusFirstFormElement = () => {
  setTimeout(() => {
    const firstInput = document.querySelector(
      'input:not([disabled]):not([tabindex="-1"])'
    );
    if (firstInput) {
      firstInput.focus();
      firstInput.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 100);
};

const focusEditButton = () => {
  setTimeout(() => {
    const editButton = document.getElementById('edit-button');
    if (editButton) {
      editButton.focus();
    }
  }, 100);
};

const navigateFromFormToButtons = (
  activeElement,
  e,
  editMode,
  isSmallScreen,
  saveButtonRef,
  cancelButtonRef,
  editButtonRef,
  selectedTab
) => {
  const tabContent = document.querySelector(".profile-tabs-content");
  if (!tabContent || !tabContent.contains(activeElement) || e.shiftKey) return;

  const focusableElements = getFocusableElements(tabContent);
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  if (activeElement === lastFocusableElement) {
    e.preventDefault();
    if (editMode) {
      // In edit mode, focus the action buttons
      if (isSmallScreen) {
        focusButtonById('save-button', 'cancel-button');
      } else {
        focusButtonById('cancel-button', 'save-button');
      }
    } else {
      // In view mode, focus the Edit button
      focusButtonById('edit-button');
    }
  }
};

const navigateFromButtons = (
  activeElement,
  e,
  editMode,
  isSmallScreen,
  saveButtonRef,
  cancelButtonRef,
  editButtonRef,
  selectedTab
) => {
  const profileActions = document.querySelector(".profile-actions");
  if (!profileActions || !profileActions.contains(activeElement)) return;

  const buttonElements = Array.from(
    profileActions.querySelectorAll("button:not([disabled])")
  );
  const currentButtonIndex = buttonElements.indexOf(activeElement);

  if (currentButtonIndex < 0) return;

  if (!e.shiftKey) {
    if (currentButtonIndex === buttonElements.length - 1) {
      e.preventDefault();
      // After last button, go to tabs on both mobile and desktop
      const tabElement = document.querySelector(`#profile-tab-${selectedTab}`);
      if (tabElement) {
        tabElement.focus();
      }
    }
  } else {
    if (currentButtonIndex === 0) {
      e.preventDefault();
      const tabContent = document.querySelector(".profile-tabs-content");
      if (tabContent) {
        const focusableElements = getFocusableElements(tabContent);
        const lastFormElement = focusableElements[focusableElements.length - 1];
        lastFormElement?.focus();
      }
    }
  }
};

export const createKeyboardNavigation = ({
  currentGroup,
  setCurrentGroup,
  selectedTab,
  setSelectedTab,
  setSelectedLabelKey,
  tabLabelKeys,
  isSmallScreen,
  editMode,
  saveButtonRef,
  cancelButtonRef,
  editButtonRef
}) => {
  const handleKeyDown = event => {
    if (currentGroup === null) return;

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      event.stopPropagation();

      const direction = event.key === "ArrowLeft" ? -1 : 1;
      const newTab = selectedTab + direction;
      const clampedTab =
        newTab < 0
          ? tabLabelKeys.length - 1
          : newTab >= tabLabelKeys.length
          ? 0
          : newTab;

      setSelectedTab(clampedTab);
      setSelectedLabelKey(tabLabelKeys[clampedTab]);
    }

    if (event.key === "Enter" || event.key === " ") {
      setCurrentGroup("content");
      const contentGroup = document.querySelector(
        '[data-navigation-group="content"]'
      );
      contentGroup?.focus();
    }

    if (event.key === "Escape") {
      setCurrentGroup(null);
    }
  };

  const handleGroupFocus = groupName => {
    setCurrentGroup(groupName);
  };

  const handleTabKeyNavigation = e => {
    if (e.key !== "Tab") return;

    const activeElement = document.activeElement;

    navigateFromFormToButtons(
      activeElement,
      e,
      editMode,
      isSmallScreen,
      saveButtonRef,
      cancelButtonRef,
      editButtonRef,
      selectedTab
    );
    navigateFromButtons(
      activeElement,
      e,
      editMode,
      isSmallScreen,
      saveButtonRef,
      cancelButtonRef,
      editButtonRef,
      selectedTab
    );
  };

  const setupTabNavigation = (selectedTab, editMode) => {
    if (selectedTab === 2) return;

    document.addEventListener("keydown", handleTabKeyNavigation);

    return () => {
      document.removeEventListener("keydown", handleTabKeyNavigation);
    };
  };

  return {
    handleKeyDown,
    handleGroupFocus,
    handleTabKeyNavigation,
    setupTabNavigation,
    focusFirstFormElement,
    focusEditButton
  };
};
